/* ==========================================================================
   BuildDirect — Flash sale
   --------------------------------------------------------------------------
   Two jobs, both small.

   1. Copy the discount code. A code promotion loses people between "I read the
      code" and "I typed the code into the checkout", and most of that loss is
      on phones. One tap removes the step. The code is real text inside a
      <button>, so if this file never loads the shopper can still read and
      select it — nothing here is load-bearing for the offer.

   2. Report on it. docs/site-audit-2026-08.md C7 is the standing complaint that
      this store cannot separate behaviours it has merged: without these events
      a flash sale's homepage placement can only be judged by total revenue over
      the same two days, which is every other thing that happened as well.

   Events pushed to dataLayer (GTM-TWC9KZRX is already installed, so events are
   dataLayer pushes — docs/landing-page-system.md):

   | Event                 | Fires on                                         |
   |-----------------------|--------------------------------------------------|
   | flash_sale_view       | Band scrolls into view (once per page view)       |
   | flash_sale_code_copy  | Code chip tapped, after the copy resolves         |
   | flash_sale_cta_click  | The band's call to action                         |

   All three carry flash_sale_code, so a campaign can be told apart from the
   next one without redeploying the tag. flash_sale_code_copy also carries
   copy_ok, because a clipboard write can be refused by the browser and a
   refused copy that reports as a success would overstate the chip.

   Listeners are delegated from the document rather than bound to the elements:
   the theme editor replaces a section's markup wholesale on every settings
   change, which detaches anything bound directly to it.
   ========================================================================== */

(function () {
  if (window.__bdFlashSaleInit) return;
  window.__bdFlashSaleInit = true;

  var BAND = '.bd-flash';
  var COPY_ATTR = 'data-bd-flash-copy';
  var CONFIRM_MS = 2600;

  function push(event, payload) {
    window.dataLayer = window.dataLayer || [];
    var data = { event: event };
    for (var k in payload || {}) {
      if (Object.prototype.hasOwnProperty.call(payload, k)) data[k] = payload[k];
    }
    window.dataLayer.push(data);
  }

  /* Code of the band a given node sits in, so every event is attributable even
     when two bands are staged on one page. */
  function codeFor(node) {
    var band = node ? node.closest(BAND) : null;
    var chip = band ? band.querySelector('[' + COPY_ATTR + ']') : null;
    return chip ? chip.getAttribute(COPY_ATTR) : '';
  }

  /* ----------------------------------------------------------------- copying */

  /*
     navigator.clipboard is unavailable on insecure origins and can reject when
     the document is not focused, so the textarea path stays as a fallback. It
     is deliberately not the primary: execCommand is deprecated and scrolls the
     page on some browsers if the node is not positioned out of flow.
  */
  function legacyCopy(text) {
    var el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.position = 'fixed';
    el.style.top = '-1000px';
    el.style.opacity = '0';
    document.body.appendChild(el);
    var ok = false;
    try {
      el.select();
      ok = document.execCommand('copy');
    } catch (e) {
      ok = false;
    }
    document.body.removeChild(el);
    return ok;
  }

  function copy(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).then(
        function () {
          return true;
        },
        function () {
          return legacyCopy(text);
        }
      );
    }
    return Promise.resolve(legacyCopy(text));
  }

  function confirmCopy(chip, ok) {
    var band = chip.closest(BAND);
    var out = band ? band.querySelector('.bd-flash__copied') : null;
    if (!out) return;

    /* The chip keeps saying "tap to copy" on failure, because it is still true —
       the shopper now has to select the code by hand, and telling them it
       copied when it did not is the one outcome worth avoiding here. */
    out.textContent = ok ? 'Copied' : 'Press and hold to copy';
    clearTimeout(out.__bdTimer);
    out.__bdTimer = setTimeout(function () {
      out.textContent = '';
    }, CONFIRM_MS);
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    var chip = target && target.closest ? target.closest('[' + COPY_ATTR + ']') : null;
    if (chip) {
      var code = chip.getAttribute(COPY_ATTR) || '';
      copy(code).then(function (ok) {
        confirmCopy(chip, ok);
        push('flash_sale_code_copy', { flash_sale_code: code, copy_ok: !!ok });
      });
      return;
    }

    var cta = target && target.closest ? target.closest('.bd-flash__cta') : null;
    if (cta && cta.getAttribute('aria-disabled') !== 'true') {
      push('flash_sale_cta_click', {
        flash_sale_code: codeFor(cta),
        link_url: cta.getAttribute('href') || ''
      });
    }
  });

  /* -------------------------------------------------------------- visibility */

  /*
     A view is "it entered the viewport", not "it exists in the document". The
     band sits above the hero, so the two are nearly the same on the homepage
     today — but the section is placeable anywhere, and a view event that fires
     for a band nobody scrolled to would quietly become a lie the moment
     somebody moves it down the page.
  */
  function watch() {
    var bands = document.querySelectorAll(BAND);
    if (!bands.length) return;

    if (!('IntersectionObserver' in window)) {
      for (var i = 0; i < bands.length; i++) {
        if (bands[i].__bdSeen) continue;
        bands[i].__bdSeen = true;
        push('flash_sale_view', { flash_sale_code: codeFor(bands[i]) });
      }
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting || entry.target.__bdSeen) return;
          entry.target.__bdSeen = true;
          observer.unobserve(entry.target);
          push('flash_sale_view', { flash_sale_code: codeFor(entry.target) });
        });
      },
      { threshold: 0.5 }
    );

    for (var j = 0; j < bands.length; j++) {
      if (!bands[j].__bdSeen) observer.observe(bands[j]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }

  /* The editor re-renders the section on every settings change; re-arm so a
     merchandiser previewing the band does not lose the observer. */
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector && event.target.querySelector(BAND)) watch();
  });
})();
